import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ILinkPreviewModel } from '@gem/models/commonModel';

import Tasks from 'APP/Tasks';
import Entities from 'STORE';
import { InputPanel } from 'STORE/InputPanelsStore/InputPanel/InputPanel';

interface ILinkPreviewPresenter {
  groupId: string;
  updateTimeout: ReturnType<typeof setTimeout> | null;
  inputPanel: InputPanel | null;
  update(text: string): void;
  searchLinks(text: string): Promise<void>;
  loadLinkPreview(link: string): Promise<ILinkPreviewModel | null>;
  setLinkPreview(searchedLink: string, linkPreview: ILinkPreviewModel | null): void;
  reset(): void;
}

const UPDATE_TIMEOUT = 500;

export const useLinkPreview = (groupId: string): ILinkPreviewPresenter => {
  const presenter = useLocalObservable<ILinkPreviewPresenter>(() => ({
    groupId,
    updateTimeout: null,

    get inputPanel(): InputPanel | null {
      return Entities.InputPanelsStore.getById(presenter.groupId);
    },

    update(text: string): void {
      if (presenter.updateTimeout) {
        presenter.reset();
      }

      presenter.updateTimeout = setTimeout(async () => {
        await presenter.searchLinks(text);
        presenter.reset();
      }, UPDATE_TIMEOUT);
    },

    async searchLinks(text: string): Promise<void> {
      if (!presenter.inputPanel) {
        return;
      }

      if (!text.trim()) {
        Tasks.messaging.resetLinkPreviewProcess(presenter.groupId);
        return;
      }

      if (presenter.inputPanel.linkPreviewCancelled || presenter.inputPanel.isLoadingPreview) {
        return;
      }

      const links: string[] = Tasks.app.searchLinks(text);
      const link: string = links[0] || '';

      if (!link) {
        Tasks.messaging.resetLinkPreviewProcess(presenter.groupId);
        return;
      }

      presenter.inputPanel.setLink(link);
      if (presenter.inputPanel.linkPreviews.get(link)) {
        presenter.inputPanel.setIsShowLinkPreview(true);
      } else if (!presenter.inputPanel.linkPreviews.has(link)) {
        const linkPreview = await presenter.loadLinkPreview(link);
        presenter.setLinkPreview(link, linkPreview);
      }
    },

    async loadLinkPreview(link: string): Promise<ILinkPreviewModel | null> {
      if (!presenter.inputPanel) {
        return null;
      }

      presenter.inputPanel.setIsLoadingPreview(true);
      const linkPreview = await Tasks.messaging.getLinkPreview(link);
      presenter.inputPanel.setIsLoadingPreview(false);

      return linkPreview;
    },

    setLinkPreview(searchedLink: string, linkPreview: ILinkPreviewModel | null): void {
      if (!presenter.inputPanel) {
        return;
      }

      if (!linkPreview) {
        Tasks.messaging.resetLinkPreviewProcess(presenter.groupId);
        return;
      }

      if (presenter.inputPanel.link === searchedLink) {
        presenter.inputPanel.linkPreviews.set(searchedLink, linkPreview);
        presenter.inputPanel.setIsShowLinkPreview(true);
      }
    },

    reset(): void {
      if (presenter.updateTimeout) {
        clearTimeout(presenter.updateTimeout);
        presenter.updateTimeout = null;
      }
    },
  }));

  useEffect(() => {
    presenter.groupId = groupId;
    return () => presenter.reset();
  }, [groupId]);

  return presenter;
};
